import React from "react";
import "./App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider, useAuth } from "./contexts/AuthContext";
import LandingPagePro from "./pages/LandingPagePro";
import LoginPage from "./pages/LoginPage";
import RegisterPage from "./pages/RegisterPage";
import DashboardPage from "./pages/DashboardPage";
import OnboardingPage from "./pages/OnboardingPage";
import AdminDashboardPage from "./pages/AdminDashboardPage";
import OrderDetailPage from "./pages/OrderDetailPage";
import TermsPage from "./pages/TermsPage";
import PrivacyPage from "./pages/PrivacyPage";
import MPStartupBenefitsPage from "./pages/MPStartupBenefitsPage";

import ErrorBoundary from "./components/ErrorBoundary";
import OutreachApp from "./outreach/OutreachApp";
import CompanyProfilePage from "./pages/CompanyProfilePage";
import FloatingHelpWidget from "./components/FloatingHelpWidget";

// Show help widget only on client pages (not admin, not outreach, not landing)
const ClientHelpWidget = () => {
  const { isAuthenticated, user } = useAuth();
  if (!isAuthenticated) return null;
  if (user?.role === 'admin' || user?.role === 'super_admin') return null;
  return <FloatingHelpWidget />;
};

// Legacy /communication URL → dashboard "tickets" section, preserving ?ticket=<id>.
const CommunicationRedirect = () => {
  const search = typeof window !== 'undefined' ? window.location.search : '';
  const ticket = new URLSearchParams(search).get('ticket');
  const target = ticket ? `/dashboard?section=tickets&ticket=${ticket}` : '/dashboard?section=tickets';
  return <Navigate to={target} replace />;
};

// Protected Route wrapper for authenticated users
const ProtectedRoute = ({ children, adminOnly = false }) => {
  const { isAuthenticated, user, loading } = useAuth();
  
  if (loading) return null;
  
  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }
  
  if (adminOnly && user?.role !== 'super_admin' && user?.role !== 'admin') {
    return <Navigate to="/dashboard" replace />;
  }
  
  return children;
};

// Public Route wrapper (redirect based on role if already logged in)
const PublicRoute = ({ children }) => {
  const { isAuthenticated, user, loading } = useAuth();
  
  if (loading) return null;
  
  if (isAuthenticated) {
    // Admins → /admin, clients → /dashboard (single shell with sidebar).
    if (user?.role === 'super_admin' || user?.role === 'admin') {
      return <Navigate to="/admin" replace />;
    }
    return <Navigate to="/dashboard" replace />;
  }
  
  return children;
};

function AppRoutes() {
  return (
    <Routes>
      {/* Public Routes */}
      <Route path="/" element={<LandingPagePro />} />
      <Route path="/login" element={<PublicRoute><LoginPage /></PublicRoute>} />
      <Route path="/register" element={<PublicRoute><RegisterPage /></PublicRoute>} />
      <Route path="/terms" element={<TermsPage />} />
      <Route path="/privacy" element={<PrivacyPage />} />
      <Route path="/mp-startup-benefits" element={<MPStartupBenefitsPage />} />
      
      {/* Protected Client Routes */}
      {/* Legacy /communication URL → dashboard "tickets" section (WhatsApp UI removed). */}
      <Route path="/communication" element={<ProtectedRoute><CommunicationRedirect /></ProtectedRoute>} />
      <Route path="/client/dashboard" element={<Navigate to="/dashboard" replace />} />
      <Route path="/dashboard" element={<ProtectedRoute><DashboardPage /></ProtectedRoute>} />
      <Route path="/onboarding" element={<ProtectedRoute><OnboardingPage /></ProtectedRoute>} />
      <Route path="/orders/:orderId" element={<ProtectedRoute><OrderDetailPage /></ProtectedRoute>} />
      <Route path="/companies/:companyId/profile" element={<ProtectedRoute><CompanyProfilePage /></ProtectedRoute>} />
      
      {/* Protected Admin Routes */}
      <Route path="/admin" element={<ProtectedRoute adminOnly><AdminDashboardPage /></ProtectedRoute>} />
      <Route path="/admin/orders/:orderId" element={<ProtectedRoute adminOnly><AdminDashboardPage /></ProtectedRoute>} />
      <Route path="/admin/*" element={<ProtectedRoute adminOnly><AdminDashboardPage /></ProtectedRoute>} />
      
      {/* Outreach Platform — completely separate app */}
      <Route path="/outreach/*" element={<OutreachApp />} />
      
      {/* Catch-all redirect */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <div className="App">
      <ErrorBoundary>
        <BrowserRouter>
          <AuthProvider>
            <AppRoutes />
            <ClientHelpWidget />
            <Toaster position="top-right" richColors />
          </AuthProvider>
        </BrowserRouter>
      </ErrorBoundary>
    </div>
  );
}

export default App;
